import { useCurrentTenant, useErrorContext } from '@qpr/client-library';
import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import axios, { AxiosError, AxiosResponse } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import AppBarContext from '../../../components/BusinessPortalAppBarContext';
import PeriodMenu, { Period } from './PeriodMenu';

interface PeriodSelectorProps {
  ignoredLevels?: string[];
  latestYears?: number;
}

const PeriodSelector = ({ ignoredLevels = [], latestYears }: PeriodSelectorProps): JSX.Element => {
  const [periodSourceChanged, setPeriodSourceChanged] = useState<Boolean>();

  const { setError } = useErrorContext();
  const { search, pathname } = useLocation();
  const history = useHistory();
  const tenant = useCurrentTenant();
  const queryString = new URLSearchParams(search);

  const { setPeriod, selectedPeriod } = useContext(AppBarContext);

  let criteria = '';
  ignoredLevels.forEach((level: string) => {
    if (criteria) criteria += ' AND ';
    criteria += `periodleveltype <> "${level}"`;
  });

  const { isLoading, error, data } = useQuery<AxiosResponse<Period[]>, AxiosError<ErrorObject>>(
    `periodselector-${queryString.get('periodSource') || tenant.metricsModelId || ''}`,
    async () => {
      if (periodSourceChanged === false) setPeriodSourceChanged(true);
      const response = await axios.post('meaquery', {
        query: `
        @model = [${queryString.get('periodSource') || tenant.metricsModelId || ''}];
        @periodlevel = @model.PeriodLevel;
        @periodlevel += @periodlevel.periods;
        @periodlevel
        `,
        sortBy: 'periodleveltype, startdate',
        attributes:
          'name, typename, periodleveltype, currentperiod, related, startdate, enddate, periodlevel, periodindex, islocked',
        criteria
      });

      return response.data;
    }
  );

  if (error && error.response) setError(error.response.data);

  const levels = data?.data?.filter((item: Period) => item.typename === 'Period Level') || [];
  const periods = data?.data?.filter((item: Period) => item.typename === 'Period') || [];

  const updatePeriodToUrl = (periodname: string): void => {
    queryString.set('period', periodname);
    if (history.location?.state) {
      history.push({ pathname, search: queryString.toString() }, history.location?.state);
    } else {
      history.push({ pathname, search: queryString.toString() });
    }
  };

  const handleSelect = (period: Period) => {
    setPeriod(period);
    updatePeriodToUrl(period.name || '');
  };

  useEffect(() => {
    const initPeriod = (): void => {
      const defaultValue =
        (queryString.get('period') &&
          periods.find((period: Period) => period.name === queryString.get('period'))) ||
        periods.find((period: Period) => period.id === levels[levels.length - 1]?.currentperiod) ||
        periods.find(
          (period: Period) => period.id === levels[levels.length - 1]?.related.split(',')[0]
        );

      setPeriodSourceChanged(false);
      setPeriod(defaultValue || {});
      if (queryString.get('period') && queryString.get('period') !== defaultValue?.name)
        updatePeriodToUrl(defaultValue?.name || '');
    };

    if (periodSourceChanged && data?.data) {
      setPeriodSourceChanged(false);
      updatePeriodToUrl('');
      initPeriod();
    } else if (
      (!selectedPeriod ||
        (!!queryString.get('period') &&
          selectedPeriod &&
          queryString.get('period') !== selectedPeriod.name)) &&
      data?.data
    )
      initPeriod();
  }, [data, queryString.get('period'), queryString.get('periodSource')]);

  return (
    <PeriodMenu
      latestYears={latestYears}
      selectedPeriod={selectedPeriod}
      isLoading={isLoading}
      periods={periods}
      levels={levels}
      handleSelect={handleSelect}
    />
  );
};

export default PeriodSelector;
